/**
 * Mapping a file's columns onto the canonical schema, and the layouts that
 * remember the result.
 */
import { z } from 'zod';
import { moduleSchema } from './modules';
import {
	canonicalTypeSchema,
	fieldTierSchema,
	fileStatusSchema,
	mappingBasisSchema,
} from './enums';

export const canonicalFieldSchema = z.object({
	field: z.string(),
	label: z.string(),
	type: canonicalTypeSchema,
	required: z.boolean(),
	tier: fieldTierSchema,
	help: z.string(),
	/* `readonly`, matching the interface this replaces: the alias list is the
     canonical schema's, not a screen's to push onto. */
	aliases: z.array(z.string()).readonly(),
});

export const proposedColumnSchema = z.object({
	sourceColumn: z.string(),
	samples: z.array(z.string()),
	dtype: z.string(),
	canonicalField: z.string().nullable(),
	/** What a money column looks like: offered, and deliberately not applied. */
	suggestion: z.string().nullable(),
	confidence: z.number().nullable(),
	needsConfirmation: z.boolean(),
	basis: mappingBasisSchema,
	reason: z.string(),
});

export const mappingViewSchema = z.object({
	fkFileId: z.string(),
	fileName: z.string(),
	status: fileStatusSchema,
	sheetName: z.string().nullable(),
	headerRow: z.number().nullable(),
	rowCount: z.number().nullable(),
	module: moduleSchema,
	schema: z.object({ name: z.string(), version: z.string() }),
	canonicalFields: z.array(canonicalFieldSchema),
	columns: z.array(proposedColumnSchema),
	confirmedVersion: z
		.object({
			pkMappingVersionId: z.string(),
			version: z.number(),
			confirmedAt: z.string().nullable(),
			fkConfirmedBy: z.string().nullable(),
		})
		.nullable(),
	/* This file's own resolved layout — always present once the file has been
     read. `name` is the honest default (`Layout N`) until a person renames it
     on confirm. */
	layout: z
		.object({
			pkLayoutId: z.string(),
			name: z.string(),
			version: z.number(),
			match: z.string().nullable(),
		})
		.nullable(),
	/* Set when one or more columns arrived pre-filled from another file's
     confirmed mapping. Always shown, so reuse is never mistaken for a guess. */
	remembered: z
		.object({
			fkLayoutId: z.string(),
			fromFileId: z.string(),
			fromFileName: z.string(),
			fromVersion: z.number(),
			confirmedAt: z.string().nullable(),
			explicit: z.boolean(),
		})
		.nullable(),
	/** Named, never counted. This is what the Confirm button's tooltip says. */
	requiredMissing: z.array(z.string()),
	canProcess: z.boolean(),
});

export const layoutRecordSchema = z.object({
	pkLayoutId: z.string(),
	fkClientId: z.string(),
	name: z.string(),
	version: z.number(),
	scope: z.string(),
	headerFingerprint: z.string(),
	effectiveFrom: z.string(),
	fkCreatedById: z.string().nullable(),
	createdAt: z.string(),
	/* The most recent confirmed mapping behind this layout, if any — a layout
     with none of these cannot be applied, only inspected. */
	confirmedMapping: z
		.object({
			fileId: z.string(),
			fileName: z.string(),
			version: z.number(),
			confirmedAt: z.string().nullable(),
		})
		.nullable(),
});

/** A remembered column layout, as the firm-configuration screens read it. */
export const layoutSchema = z.object({
	pkLayoutId: z.string(),
	fkClientId: z.string(),
	name: z.string(),
	version: z.number(),
	scope: z.enum(['CLIENT', 'FIRM']),
	headerFingerprint: z.string(),
	effectiveFrom: z.string(),
	fkCreatedById: z.string().nullable(),
	createdAt: z.string(),
});

export type CanonicalField = z.infer<typeof canonicalFieldSchema>;
export type ProposedColumn = z.infer<typeof proposedColumnSchema>;
export type MappingView = z.infer<typeof mappingViewSchema>;
export type LayoutRecord = z.infer<typeof layoutRecordSchema>;
export type Layout = z.infer<typeof layoutSchema>;
